/**
 * Pure-logic core for the user-script custom-widget builder.
 *
 * The builder UI (`custom-widget-builder.tsx`) collects a small form — a
 * label, a data-source endpoint, a value expression (see
 * `custom-widget-script.ts`), and presentation options — and this module
 * turns that form state into a validated `WidgetDescriptor` plus a
 * persisted "custom widget definition" the runtime can replay.
 *
 * Kept React-free so the validation + descriptor-assembly is unit-tested
 * directly with `node --test`, mirroring `widget-descriptor.ts`.
 */

import type { WidgetDescriptor, WidgetSize } from '@tyndo/types';
import { createWidgetDescriptor } from './widget-descriptor.ts';
import {
  validateScript,
  type ValidationIssue,
  type ScriptValue,
} from './custom-widget-script.ts';

// ────────────────────────────────────────────────────────────────────────
// Builder form state — what the UI binds to.
// ────────────────────────────────────────────────────────────────────────

/** How the evaluated scalar is rendered in the widget body. */
export type CustomWidgetFormat = 'number' | 'percent' | 'currency' | 'text' | 'duration';

export interface CustomWidgetDraft {
  /** Human label shown in the widget title bar + gallery. */
  label: string;
  /** Owning module id, e.g. "tyndo.production". */
  moduleId: string;
  /** Read permission required to mount the widget. */
  readPermission: string;
  /** GET endpoint the widget polls for its data record. */
  endpointPath: string;
  /** Poll interval, ms. 0 / undefined → default 60s. */
  pollMs?: number;
  /** The value expression evaluated against the endpoint response. */
  valueScript: string;
  /** Presentation format for the evaluated value. */
  format: CustomWidgetFormat;
  /** Optional unit suffix (e.g. "units/hr"). */
  unit?: string;
  /** Grid size. */
  size?: WidgetSize;
  /**
   * Allow-list of top-level field names available from the data source,
   * used to validate the script's field references. Optional.
   */
  availableFields?: string[];
}

/**
 * Canonical persisted form of a user-authored widget. The runtime replays
 * this: fetch `endpointPath`, evaluate `valueScript` against the response,
 * format per `format`/`unit`. Stored alongside the dashboard layout.
 */
export interface CustomWidgetDefinition {
  widgetId: string;
  label: string;
  moduleId: string;
  endpointPath: string;
  valueScript: string;
  format: CustomWidgetFormat;
  unit?: string;
}

export interface BuildResult {
  ok: boolean;
  /** Field-keyed validation issues. Empty when `ok`. */
  errors: Record<string, string>;
  /** Positional script issues (for editor underlines). */
  scriptIssues: ValidationIssue[];
  /** The assembled descriptor when `ok`, else null. */
  descriptor: WidgetDescriptor | null;
  /** The persisted definition when `ok`, else null. */
  definition: CustomWidgetDefinition | null;
}

const SLUG_RE = /[^a-z0-9]+/g;

/** Derive a stable widget id from the module + label. Exported for tests. */
export function deriveWidgetId(moduleId: string, label: string): string {
  const modSlug = moduleId.replace(/^tyndo\./, '').replace(SLUG_RE, '_').replace(/^_|_$/g, '');
  const labelSlug = label.toLowerCase().replace(SLUG_RE, '_').replace(/^_|_$/g, '');
  return `${modSlug || 'custom'}.widget.custom_${labelSlug || 'widget'}`;
}

function isHttpishPath(path: string): boolean {
  return path.startsWith('/') || path.startsWith('http://') || path.startsWith('https://');
}

/**
 * Validate a draft and, when valid, assemble the descriptor + definition.
 * Pure + total — never throws; every problem comes back keyed by field.
 */
export function buildCustomWidget(draft: CustomWidgetDraft): BuildResult {
  const errors: Record<string, string> = {};

  const label = draft.label?.trim() ?? '';
  if (label.length === 0) errors.label = 'A widget label is required.';
  else if (label.length > 80) errors.label = 'Label must be 80 characters or fewer.';

  if (!draft.moduleId?.trim()) errors.moduleId = 'A module id is required.';
  if (!draft.readPermission?.trim()) errors.readPermission = 'A read permission is required.';

  const endpointPath = draft.endpointPath?.trim() ?? '';
  if (endpointPath.length === 0) {
    errors.endpointPath = 'A data-source endpoint is required.';
  } else if (!isHttpishPath(endpointPath)) {
    errors.endpointPath = 'Endpoint must be an absolute path (starts with "/") or URL.';
  }

  if (draft.pollMs != null && (!Number.isFinite(draft.pollMs) || draft.pollMs < 0)) {
    errors.pollMs = 'Poll interval must be a non-negative number of milliseconds.';
  }

  // Script validation — the substantive part.
  const scriptResult = validateScript(draft.valueScript ?? '', {
    allowedFields: draft.availableFields,
  });
  const scriptIssues = scriptResult.issues;
  if (!scriptResult.ok) {
    errors.valueScript =
      scriptResult.issues[0]?.message ?? 'The value expression is invalid.';
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors, scriptIssues, descriptor: null, definition: null };
  }

  const widgetId = deriveWidgetId(draft.moduleId.trim(), label);
  const size = draft.size ?? 'small';

  const descriptor = createWidgetDescriptor({
    widgetId,
    label,
    componentName: 'CustomScriptWidget',
    moduleId: draft.moduleId.trim(),
    description: `User-authored widget — value = ${draft.valueScript.trim()}`,
    permission: { read: draft.readPermission.trim() },
    dataSource: {
      endpoint: { method: 'GET', path: endpointPath },
      pollMs: draft.pollMs && draft.pollMs > 0 ? draft.pollMs : undefined,
    },
    supportedSizes: [size],
    defaultSize: size,
    // Stash the user script + presentation in the forward-compatible
    // configSchema bag so the runtime can replay it.
    configSchema: {
      kind: 'custom-script',
      valueScript: draft.valueScript.trim(),
      format: draft.format,
      unit: draft.unit?.trim() || undefined,
    },
  });

  const definition: CustomWidgetDefinition = {
    widgetId,
    label,
    moduleId: draft.moduleId.trim(),
    endpointPath,
    valueScript: draft.valueScript.trim(),
    format: draft.format,
    unit: draft.unit?.trim() || undefined,
  };

  return { ok: true, errors: {}, scriptIssues, descriptor, definition };
}

// ────────────────────────────────────────────────────────────────────────
// Presentation — format an evaluated scalar for display. Shared by the
// builder preview and (eventually) the runtime.
// ────────────────────────────────────────────────────────────────────────

/**
 * Format an evaluated script value per the chosen format + unit. Pure +
 * locale-stable (fixed 'en-US' grouping) so previews are deterministic.
 */
export function formatWidgetValue(
  value: ScriptValue,
  format: CustomWidgetFormat,
  unit?: string,
): string {
  if (value === null || value === undefined) return '—';

  const withUnit = (s: string): string => (unit ? `${s} ${unit}` : s);

  switch (format) {
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(n)) return String(value);
      return withUnit(n.toLocaleString('en-US'));
    }
    case 'percent': {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(n)) return String(value);
      // Treat values in [0,1] as fractions, otherwise as already-scaled %.
      const pct = Math.abs(n) <= 1 ? n * 100 : n;
      return `${pct.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
    }
    case 'currency': {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(n)) return String(value);
      return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }
    case 'duration': {
      const secs = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(secs)) return String(value);
      const s = Math.max(0, Math.round(secs));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const rem = s % 60;
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m ${rem}s`;
      return `${rem}s`;
    }
    case 'text':
    default:
      return withUnit(String(value));
  }
}
